import Button from "@/components/common/Button";
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  UserMinusIcon
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

interface EmployeeRowProps {
  employee: any;
  departments: any[];
  onTerminateEmployee: (employeeId: string) => void;
  isDepartmentAdmin: boolean;
  isTablet?: boolean;
}

const EmployeeRow = ({
  employee,
  departments,
  onTerminateEmployee,
  isDepartmentAdmin,
  isTablet = false,
}: EmployeeRowProps) => {
  const { t } = useTranslation();


  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case "company_admin":
        return t("admin.companyAdmin", "Company Admin");
      case "department_admin":
        return t("admin.departmentAdmin", "Department Admin");
      case "employee":
        return t("common.employee", "Employee");
      default:
        return role;
    }
  };

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
      <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2 lg:gap-3">
          <div className="flex-shrink-0 h-10 w-10 lg:h-12 lg:w-12">
            <div className="h-10 w-10 lg:h-12 lg:w-12 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900 dark:to-primary-800 flex items-center justify-center ring-2 ring-gray-200 dark:ring-gray-700 shadow-sm">
              <span className="text-sm lg:text-lg font-bold text-primary-700 dark:text-primary-300">
                {employee.fullName?.charAt(0) || "?"}
              </span>
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm lg:text-base font-semibold text-gray-900 dark:text-white truncate">
              {employee.fullName || "Unknown"}
            </div>
            <div className="text-xs lg:text-sm text-gray-500 dark:text-gray-400 truncate hidden lg:block">
              ID: {employee._id.slice(-8)}
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
        <div className="text-sm lg:text-base text-gray-900 dark:text-white truncate max-w-[150px] lg:max-w-none">
          {employee.email || "No email"}
        </div>
      </td>
      <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
        <span className="inline-flex items-center px-2 lg:px-2.5 py-1 rounded-full text-xs lg:text-sm font-semibold bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 dark:from-blue-900 dark:to-blue-800 dark:text-blue-200 shadow-sm">
          {getRoleDisplayName(employee.role)}
        </span>
      </td>
      {!isTablet && (
        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {(() => {
              // Find the department name for this employee
              if (employee.role === "company_admin") {
                return (
                  <span className="text-gray-400 italic">
                    {t("admin.noDepartment", "No Department")}
                  </span>
                );
              }

              if (!employee.departmentId) {
                return (
                  <span className="text-yellow-600 dark:text-yellow-400 italic">
                    {t("admin.unassigned", "Unassigned")}
                  </span>
                );
              }

              // For department admins, show their department name
              if (isDepartmentAdmin) {
                const department = Array.isArray(departments) ? departments.find(dept => dept._id === employee.departmentId) : null;
                return department ? (
                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                    {department.name}
                  </span>
                ) : (
                  <span className="text-gray-400 italic">
                    {t("common.loading", "Loading...")}
                  </span>
                );
              }

              // For company admins, find department from departments array
              console.log("EmployeeRow debugging:", {
                employeeId: employee._id,
                employeeDepartmentId: employee.departmentId,
                departments,
                isDepartmentsArray: Array.isArray(departments),
                departmentsLength: Array.isArray(departments) ? departments.length : 'not array'
              });
              
              const department = Array.isArray(departments) ? departments.find(dept => 
                (dept._id || dept.id) === employee.departmentId
              ) : null;
              
              console.log("Found department:", department);

              return department ? (
                <span className="text-blue-600 dark:text-blue-400 font-medium">
                  {department.name}
                </span>
              ) : (
                <span className="text-red-600 dark:text-red-400 italic">
                  {t("admin.departmentNotFound", "Department Not Found")}
                </span>
              );
            })()}
          </div>
        </td>
      )}
      <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-1.5 lg:gap-2">
          {employee.isActive ? (
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          ) : (
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
          )}
          <span className={`text-xs lg:text-sm font-semibold ${
            employee.isActive 
              ? "text-green-700 dark:text-green-300" 
              : "text-red-700 dark:text-red-300"
          }`}>
            {employee.isActive ? t("common.active", "Active") : t("common.inactive", "Inactive")}
          </span>
        </div>
      </td>
      <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-right">
        <div className="flex items-center justify-end">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<UserMinusIcon className="h-3 w-3 lg:h-4 lg:w-4" />}
            onClick={() => onTerminateEmployee(employee._id)}
            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20 text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-1.5 touch-manipulation active:scale-95"
          >
            <span className="hidden lg:inline">{t("admin.terminate", "Terminate")}</span>
            <span className="lg:hidden">End</span>
          </Button>
        </div>
      </td>
    </tr>
  );
};

export default EmployeeRow;
